import { lightBlue } from "@mui/material/colors";
import { styled } from "@mui/material/styles";

/**
 * The one way a changed value is drawn: the old value struck through, the new value in the blue-line
 * tone. Every surface that shows "this became that" renders through here — the per-field change
 * history, the Changes pane, and the submit/tranche dialogs — so a change reads identically wherever
 * the user meets it.
 *
 * This is the visual half of the rule `proposal-change-description.ts` already owns for wording
 * (strategy rule S1: one rule, one implementation). The submit dialogs previously showed only the new
 * value as plain grey text, so the same change looked like two different things depending on whether
 * the user was reading the history or deciding what to send.
 */

export const ProposalChangeRemovedValue = styled("del")(({ theme }) => ({
  color: theme.palette.text.disabled,
  marginRight: theme.spacing(0.5),
  textDecoration: "line-through",
}));

export const ProposalChangeAddedValue = styled("ins")(() => ({
  color: lightBlue[700],
  textDecoration: "none",
}));

const ValueContainer = styled("span")(() => ({
  display: "block",
}));

export type ProposalChangeValueRedlineProps = {
  /** The value being replaced; omitted when the change adds something that was not there before. */
  previousValue?: string;
  /** The value being proposed; omitted when the change removes something. */
  nextValue?: string;
};

/**
 * Renders a value transition as a redline. Either half may be absent — an addition has no previous
 * value and a removal has no next one — and when both are absent nothing renders at all, which is what
 * keeps a caller from having to special-case an empty change.
 */
export default function ProposalChangeValueRedline({
  previousValue,
  nextValue,
}: Readonly<ProposalChangeValueRedlineProps>) {
  if (previousValue === undefined && nextValue === undefined) return null;

  return (
    <ValueContainer>
      {previousValue !== undefined && <ProposalChangeRemovedValue>{previousValue}</ProposalChangeRemovedValue>}
      {nextValue !== undefined && <ProposalChangeAddedValue>{nextValue}</ProposalChangeAddedValue>}
    </ValueContainer>
  );
}
